import { useEffect, useState, useMemo } from 'react';
import styled from '@emotion/styled';
import {
  Form,
  FormProps,
  Input,
  Row,
  Col,
  AutoComplete,
  AutoCompleteProps,
  Button,
  Select,
} from 'antd';
import chatttSDK from 'services/chattt';
import Icon, { CloseOutlined } from '@ant-design/icons';
import { css, cx } from '@emotion/css';
import { isValidPhoneNumber } from 'libphonenumber-js/mobile';
import { localPhoneParse, phoneCodeRegionsMap, phoneRegionCodesMap } from 'utils/phoneHelpers';
import { isQuickChat, isTfsoftware } from 'productLine';
import PhoneRegionSelect from '../components/PhoneRegionSelect';
import { ReactComponent as LockSvg } from '../assets/lock.svg';
import { loginCountryCodeCache, loginTypeCache } from 'services/cache';
import { TFnType, useTranslate } from '@tolgee/react';
import { useQuery } from '../../../components/RouterQuery';
const vaultKey = isQuickChat ? 'quickchat' : isTfsoftware ? 'tfsoftware' : 'fett';

const pcItem = css`
  margin-bottom: 0;
  .ant-input-group {
    display: flex;
  }
`;
const pcInput = css`
  flex: 1;
  .ant-select {
    width: 100% !important;
  }
`;

export interface Values {
  account: string;
  password: string;
  countryCode?: string;
  code?: string;
}
export interface LoginFormProps extends FormProps<Values> {}

const FormItem = Form.Item;

const accountLabel = css`
  &.ant-select-single.ant-select-sm:not(.ant-select-customize-input) .ant-select-selector {
    padding-left: 0;
  }

  &:hover {
    color: var(--ant-primary-color);
  }
  .ant-select-arrow {
    color: var(--fett-text-secondary);
  }
`;
export const Line = styled(Row)`
  padding: 4px 8px;
  border: 1px solid transparent;
  border-radius: 4px;
  &:hover {
    border-color: #91d5ff;
    background-color: var(--fett-surface-info-bg);
  }
`;
export const DelBtn = styled(Button)`
  margin: 0 4px;
  background: none !important;
  padding: 0;
  color: #ccc;
`;
const LoginForm = (props: LoginFormProps) => {
  const { form, onValuesChange } = props;
  const { t } = useTranslate();
  const [memos, setMemos] = useState<Chattt.ValutCredential[]>([]);
  const [open1, setOpen1] = useState(false);
  const [open2, setOpen2] = useState(false);

  const [loginType, setLoginType] = useState<'accountOrEmail' | 'mobile'>(
    isQuickChat ? 'mobile' : (loginTypeCache.get() as any) || 'accountOrEmail',
  );
  const { email } = useQuery();
  useEffect(() => {
    if (!!email) {
      setLoginType('accountOrEmail');
    }
  }, [email]);
  const [country, setCountry] = useState<General.PhoneRegions>('cn');
  const [countryCode, setCountryCode] = useState<General.PhoneRegionCodes>(() => {
    const regoinOrCode = loginCountryCodeCache.get() || ('' as any);
    if (phoneCodeRegionsMap[regoinOrCode]) {
      return regoinOrCode as General.PhoneRegionCodes;
    }
    // @ts-ignore
    const code = phoneRegionCodesMap[regoinOrCode] || '+86';
    return code;
  });

  const mobileValidate = (mobile: string, t: TFnType, country: string, countryCode: string) => {
    let error;
    if (!mobile || !isValidPhoneNumber(`${countryCode}${mobile}`, country as any)) {
      error = t?.('ViewStartups.registry.form.item.account.validator.error');
    }
    return error;
  };

  useEffect(() => {
    if (!chatttSDK?.vault) {
      return;
    }
    chatttSDK.vault
      .read(vaultKey)
      .then((resp) => {
        setMemos(resp);
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.log('e', e);
      });
  }, []);
  const autoCompletesProps = useMemo(() => {
    const options: AutoCompleteProps['options'] = memos.map((cred) => {
      return {
        options: [],
        value: cred.account,
        label: (
          <Row align="middle" justify="space-between">
            <Col span={22}>
              <Line
                align="middle"
                onClick={() => {
                  if (form?.setFieldsValue && chatttSDK?.vault) {
                    const phonedata = localPhoneParse(cred.account);
                    if (phonedata) {
                      const values = {
                        account: phonedata.phone,
                        countryCode: phonedata.code,
                        password: cred.password,
                      };
                      form.setFieldsValue(values);
                      onValuesChange && onValuesChange(values, values);
                      setLoginType('mobile');
                    } else {
                      if (!isQuickChat) {
                        setLoginType('accountOrEmail');
                        const values = { account: cred.account, password: cred.password };
                        form.setFieldsValue(values);
                        onValuesChange && onValuesChange(values, values);
                      }
                    }
                    setOpen1(false);
                    setOpen2(false);
                    chatttSDK.vault
                      .getPwd(cred.account, vaultKey)
                      .then((resp) => {
                        if (!resp) {
                          return;
                        }
                        const phonedata = localPhoneParse(resp?.account);
                        if (phonedata) {
                          setLoginType('mobile');
                          const values = {
                            account: phonedata.phone,
                            countryCode: phonedata.code,
                            password: resp.password,
                          };

                          setCountry(phoneCodeRegionsMap[phonedata.code]);
                          setCountryCode(phonedata.code);
                          form.setFieldsValue(values);
                          onValuesChange && onValuesChange(values, values);
                        } else {
                          if (!isQuickChat) {
                            setLoginType('accountOrEmail');
                            const values = { account: resp.account, password: resp.password };
                            form.setFieldsValue(values);
                            onValuesChange && onValuesChange(values, values);
                          }
                        }
                        form.validateFields();
                        setOpen1(false);
                        setOpen2(false);
                      })
                      .catch((e) => {
                        // eslint-disable-next-line no-console
                        console.log('e', e);
                      });
                  }
                }}
                key={cred.account}
                justify="space-between"
              >
                <Col>{cred.account}</Col>
                <Col>{cred.password}</Col>
              </Line>
            </Col>
            <Col span={2}>
              <DelBtn
                type="text"
                onClick={async () => {
                  try {
                    if (chatttSDK?.vault) {
                      const fullCred = await chatttSDK.vault.getPwd(cred.account, vaultKey);
                      if (fullCred) {
                        const result = await chatttSDK.vault.remove(cred.account, vaultKey);
                        if (result) {
                          setMemos((m) => m.filter((t) => t.account !== cred.account));
                        }
                      }
                    }
                  } catch (e) {
                    // do nothing
                  }
                }}
              >
                <CloseOutlined />
              </DelBtn>
            </Col>
          </Row>
        ),
      };
    });
    return {
      options,
    } as AutoCompleteProps;
  }, [form, memos, onValuesChange]);
  useEffect(() => {
    loginTypeCache.set(loginType);
  }, [loginType]);
  const accountSelectLabel = (
    <Select
      className={accountLabel}
      size="small"
      value={loginType}
      onChange={(value) => {
        setLoginType(value);
        form?.resetFields();
      }}
      dropdownMatchSelectWidth={false}
      dropdownStyle={{ width: '136px' }}
      bordered={false}
    >
      <Select.Option key="accountOrEmail" value="accountOrEmail">
        {t('ViewStartups.login.form.typeAccountOrEmail')}
      </Select.Option>
      <Select.Option key="mobile" value="mobile">
        {t('ViewStartups.login.form.typeMobile')}
      </Select.Option>
    </Select>
  );
  const pcItemReset = isQuickChat
    ? css`
        .ant-form-item {
          margin-bottom: 16px !important;
        }
      `
    : css``;

  const pwdReset = isQuickChat
    ? css`
        margin-bottom: 0px !important;
      `
    : css``;

  return (
    <Form {...props}>
      <FormItem
        className={cx(pcItem, pcItemReset)}
        label={isQuickChat ? null : accountSelectLabel}
        shouldUpdate
      >
        <Input.Group compact>
          {loginType === 'mobile' ? (
            <FormItem name="countryCode" initialValue={countryCode}>
              <PhoneRegionSelect
                className={css`
                  width: 110px !important;
                `}
                onChange={(regionCode, region) => {
                  setCountry(region);
                  setCountryCode(regionCode);
                  form?.setFieldsValue({ countryCode: regionCode });
                  loginCountryCodeCache.set(regionCode);
                  form?.validateFields(['account']);
                }}
              />
            </FormItem>
          ) : null}
          <FormItem
            name="account"
            className={pcInput}
            rules={
              loginType === 'mobile'
                ? [
                    {
                      validator: (_, mobile) => {
                        if (!mobile) {
                          return Promise.resolve();
                        }
                        const error = mobileValidate(mobile, t, country, countryCode);
                        return error ? Promise.reject(new Error(error)) : Promise.resolve();
                      },
                      validateTrigger: 'onBlur',
                    },
                  ]
                : []
            }
          >
            <AutoComplete open={open1} {...autoCompletesProps}>
              <Input
                onFocus={() => {
                  setOpen1(true);
                }}
                onBlur={() => {
                  setOpen1(false);
                }}
                placeholder={
                  loginType === 'mobile'
                    ? t('ViewStartups.login.form.item.mobile.placeholder')
                    : t('ViewStartups.login.form.item.accountEmail.placeholder')
                }
              />
            </AutoComplete>
          </FormItem>
        </Input.Group>
      </FormItem>
      <FormItem
        className={pwdReset}
        name="password"
        label={isQuickChat ? null : t('ViewStartups.login.form.item.password.label')}
      >
        <AutoComplete open={open2} {...autoCompletesProps}>
          <Input.Password
            prefix={isQuickChat ? <Icon component={LockSvg} /> : null}
            onFocus={() => {
              setOpen2(true);
            }}
            onBlur={() => {
              setOpen2(false);
            }}
            placeholder={t('ViewStartups.login.form.item.password.placeholder')}
          />
        </AutoComplete>
      </FormItem>
    </Form>
  );
};
export default LoginForm;
